import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Output,
} from '@angular/core';
import { Observable, combineLatest, filter, map } from 'rxjs';
import { CommonModule, DatePipe } from '@angular/common';
import {
  BasisInfoMobileComponent,
  TBasisInfoMobileItem,
} from '../../basis-info-mobile/basis-info-mobile.component';
import { BasisBestPriceMobilePriceOptionsContainer } from '../../basis-best-price-mobile/basis-best-price-mobile-options/basis-best-price-mobile-price-options.container';
import { MobileDetailsCardComponent } from '@prlw/common/mobile-details-card/mobile-details-card.component';
import {
  MOBILE_OVERLAY_DATA_TOKEN,
  MobileOverlayService,
} from '@prlw/libs/mobile-overlay/mobile-overlay.service';
import { MainTableService } from '../../table/main-table/main-table.service';
import {
  BasisBestPriceMobilePriceOptionsService,
  EDisplayPriceTariffOption,
} from '@prlw/core/monitor/basis-best-price-mobile-price-options.service';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { delimitNumber } from '@prlw/libs/delimit-number';
import { BasisDetailsMobileComponent } from './basis-details-mobile.component';

@Component({
  selector: 'prlw-basis-details-mobile',
  standalone: true,
  imports: [
    CommonModule,
    MobileDetailsCardComponent,
    BasisDetailsMobileComponent,
  ],
  providers: [DatePipe],
  templateUrl: './basis-details-mobile.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasisDetailsMobileContainer {
  @Output() eventClose = new EventEmitter<void>();

  constructor(
    @Inject(MOBILE_OVERLAY_DATA_TOKEN)
    public readonly instrumentCode$: Observable<string>,
    private readonly mainTableService: MainTableService,
    private readonly mobileOverlayService: MobileOverlayService,
    private readonly basisBestPriceMobilePriceOptionsService: BasisBestPriceMobilePriceOptionsService,
    private readonly datePipe: DatePipe,
  ) {}

  item$ = combineLatest([
    this.mainTableService.sortedInstruments$,
    this.instrumentCode$,
  ]).pipe(
    map(
      ([sortedInstruments, instrumentCode]) =>
        sortedInstruments.find(
          (sortedInstrument) =>
            sortedInstrument.instrumentCode === instrumentCode,
        ) ?? null,
    ),
    filter((item): item is Instrument => item !== null),
  );

  briefDescription$ = this.item$.pipe(
    map((item) => item.shipment.briefDescription),
  );

  greyTopItems$ = this.item$.pipe(
    map((item) => [
      {
        label: 'Тариф',
        first: item.tariff === null ? '-' : delimitNumber(item.tariff),
      },
      {
        label: 'Объем',
        first: `${String(item.currentAmount)} ${String(item.expectedAmount)}`,
      },
      {
        label: 'В пути',
        first: String(item.deliveryDays),
      },
    ]),
  );

  whiteTopItems$ = this.item$.pipe(
    map((item) => [
      {
        label: 'Предложение',
        first: delimitNumber(item.quote.sellPrice),
        second: String(item.quote.sellAmount),
      },
      {
        label: 'Сделка',
        first: delimitNumber(item.quote.lastDealPrice),
        diff: item.quote.delta,
      },
      {
        label: 'Спрос',
        first: delimitNumber(item.quote.buyPrice),
        second: String(item.quote.buyAmount),
      },
    ]),
  );

  whiteBottomItems$ = this.item$.pipe(
    map((item) => [
      {
        label: 'Отгрузка',
        first: item.shipmentDays === null ? '-' : String(item.shipmentDays),
      },
      {
        label: 'Начало продаж',
        first: `~ ${this.datePipe.transform(
          item.expectedTradingStart,
          'HH:mm',
        )}`,
      },
    ]),
  );

  priceOptionsParam$ = this.item$.pipe(
    filter((item): item is Instrument => !!item),
    map((item) => {
      if (!item.tariff)
        return {
          dealTariffPrice: null,
          buyTariffPrice: null,
          sellTariffPrice: null,
        };
      return {
        dealTariffPrice: item.quote.lastDealPrice
          ? item.quote.lastDealPrice + item.tariff
          : null,
        buyTariffPrice: item.quote.buyPrice
          ? item.quote.buyPrice + item.tariff
          : null,
        sellTariffPrice: item.quote.sellPrice
          ? item.quote.sellPrice + item.tariff
          : null,
      };
    }),
  );

  priceOption$ = combineLatest([
    this.priceOptionsParam$,
    this.basisBestPriceMobilePriceOptionsService.currentIdTextItem$,
  ]).pipe(
    map(
      ([
        { dealTariffPrice, buyTariffPrice, sellTariffPrice },
        currentIdTextItem,
      ]) => {
        if (!currentIdTextItem) return null;
        return {
          text: currentIdTextItem.text,
          value:
            currentIdTextItem.id === EDisplayPriceTariffOption.DEAL
              ? dealTariffPrice
              : currentIdTextItem.id === EDisplayPriceTariffOption.BUY
                ? buyTariffPrice
                : sellTariffPrice,
        };
      },
    ),
  );

  handleClickPriceOptions() {
    this.mobileOverlayService.show(
      BasisBestPriceMobilePriceOptionsContainer,
      this.priceOptionsParam$,
    );
  }

  getBasisInfoParam(item: Instrument): TBasisInfoMobileItem {
    return {
      basisCode: item.shipment.code,
      basisName: item.shipment.name,
      plantName: item.shipment.plants[0] ? item.shipment.plants[0].name : '-',
      station: item.shipmentBasisStations[0]
        ? item.shipmentBasisStations[0].name
        : '-',
      instrumentCode: item.instrumentCode,
      instrumentName: item.instrumentName,
    };
  }

  handleClickAboutBasis() {
    this.mobileOverlayService.show(
      BasisInfoMobileComponent,
      this.item$.pipe(map((item) => this.getBasisInfoParam(item))),
    );
  }
}
